import { moneyFormat } from '@4beefopag/4bee-fopag-core/dist/util';
import {
  DownloadOutlined,
  EditOutlined,
  FileSearchOutlined,
  MailOutlined,
  SendOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { faEnvelopeOpen } from '@fortawesome/pro-light-svg-icons';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown } from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { markPayrollAsUnseen, markPayrollAsViewed } from '../../../services/repository/payrollRepository';
import { PAYROLL, TRANSMITTABLE_PAYROLL_STATUS_IDS } from '../../../services/util/globalConstant';
import Button from '../../atoms/Button';
import ObservacaoColumn from './ObservacaoColumn';
import { APONTAMENTO } from '../../../services/apontamentoProducao';
import { Subjects, useUserPolicy } from '../../../user-policy';

const ButtonsColumn = styled.td`
  text-align: center !important;

  > button {
    padding-left: 0;
    padding-right: 0;
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  color: #656668;

  ${(props) => {
    if (props.isViewed) {
      return `
        background: #F4F8FF;
      `;
    }
    return `
      font-weight: 600;
    `;
  }}
`;

function PayrollTableRow({
  payroll,
  onEditClick,
  onTransmitClick,
  isAdmin,
  onBaixarArquivoClick,
  onEarlyPayment,
  canShowApdataInfo,
}) {
  const { userAccount } = useUserPolicy();
  const canTransmitPayroll =
    userAccount?.ability?.can('process', Subjects.PAYROLL_TRANSMISSION) &&
    TRANSMITTABLE_PAYROLL_STATUS_IDS.includes(payroll.status);
  const detailPayrollLink = `/payrolls/${payroll.id}`;

  const [rowClickStartTime, setRowClickStartTime] = useState();
  const [isTransmitting, setIsTransmitting] = useState();
  const [isViewed, setIsViewed] = useState(
    payroll.latestOperation && payroll.latestOperation.seenAt.isAfter(payroll.atualizadoEm)
  );

  const handleOnClickRow = () => {
    const now = new Date();
    const difference = Math.abs(rowClickStartTime.getTime() - now.getTime());
    const wasClickedAndDragged = difference > 200;
    if (wasClickedAndDragged) {
      return;
    }

    const newTab = window.open(detailPayrollLink, '_blank');
    newTab.focus();
    setIsViewed(true);
  };

  const handleUpdateVisualizationStatusClick = async () => {
    if (isViewed) {
      await markPayrollAsUnseen(payroll.id);
      setIsViewed(false);
    }

    if (!isViewed) {
      await markPayrollAsViewed(payroll.id);
      setIsViewed(true);
    }
  };

  const menuItems = [
    {
      label: (
        <span data-testid="botao-baixar-planilha">
          <DownloadOutlined /> &nbsp; Baixar Apontamento
        </span>
      ),
      disabled: !payroll.canDownloadWorksheet,
      onClick: () => onBaixarArquivoClick(payroll, 'planilha'),
    },
    {
      label: (
        <span data-testid="botao-baixar-anexo">
          <DownloadOutlined /> &nbsp; Baixar Anexos ({payroll.files ? payroll.files.length : 0})
        </span>
      ),
      disabled: payroll.files && payroll.files.length === 0,
      onClick: () => onBaixarArquivoClick(payroll, 'anexos'),
    },
    {
      label: (
        <span>
          <CalendarOutlined /> &nbsp; Alterar Pagamento
        </span>
      ),
      disabled: payroll.status !== APONTAMENTO.status.faturada && !isAdmin,
      onClick: () => onEarlyPayment(payroll),
    },
    {
      label: (
        <span>
          <FileSearchOutlined /> &nbsp;Visualizar Requisição
        </span>
      ),
      onClick: () => {
        const newTab = window.open(detailPayrollLink, '_blank');
        newTab.focus();
      },
    },
  ];

  if (isAdmin) {
    menuItems.push({
      label: (
        <span data-testid="edit-payroll-button">
          <EditOutlined /> &nbsp; Editar Requisição
        </span>
      ),
      onClick: () => onEditClick(payroll),
    });
  }

  if (canTransmitPayroll) {
    menuItems.push({
      label: (
        <span data-testid="transmit-payroll-button">
          <SendOutlined /> &nbsp; Transmitir para Apdata
        </span>
      ),
      disabled: isTransmitting,
      onClick: async () => {
        try {
          setIsTransmitting(true);
          await onTransmitClick(payroll);
        } finally {
          setIsTransmitting(false);
        }
      },
    });
  }

  if (isViewed) {
    menuItems.push({
      label: (
        <span>
          <MailOutlined /> &nbsp;Marcar como não lido
        </span>
      ),
      onClick: handleUpdateVisualizationStatusClick,
    });
  } else {
    menuItems.push({
      label: (
        <span>
          <FontAwesomeIcon icon={faEnvelopeOpen} /> &nbsp; Marcar como lido
        </span>
      ),
      onClick: handleUpdateVisualizationStatusClick,
    });
  }

  return (
    <TableRow
      key={payroll.id}
      onClick={handleOnClickRow}
      onMouseDown={() => setRowClickStartTime(new Date())}
      title="Clique para detalhar a requisição."
      isViewed={isViewed}
    >
      <td>{payroll.atualizadoEm && payroll.atualizadoEm.format('DD/MM/YYYY HH:mm')}</td>
      <td>{payroll.id}</td>
      {canShowApdataInfo && <td>{payroll.gaTransactionLogId}</td>}
      <td>{payroll.folha && `<${payroll.folha.id}> ${payroll.folha.name}`}</td>
      <td>{PAYROLL.status[payroll.status]}</td>
      <td>{payroll.dataPrevistaFatura && payroll.dataPrevistaFatura.format('DD/MM/YYYY')}</td>
      <td>{payroll.faturadoEm && payroll.faturadoEm.format('DD/MM/YYYY')}</td>
      <td>{payroll.dataPrevistaPagamentoCooperado && payroll.dataPrevistaPagamentoCooperado.format('DD/MM/YYYY')}</td>
      <td>
        {payroll.paymentDateAdjustedByManager ? payroll.paymentDateAdjustedByManager.format('DD/MM/YYYY') : '---'}
      </td>
      <td>{payroll.folha && payroll.folha.carteiraFolha}</td>
      <td>{moneyFormat(payroll.totalLiquido)}</td>
      <ObservacaoColumn observacao={payroll.observacao} />
      {isAdmin && <ObservacaoColumn observacao={payroll.observacaoAdmin} />}
      {/* Stop propagation necessário para não disparar o onClick do elemento pai que redireciona para a tela de detalhes: */}
      <ButtonsColumn onClick={(e) => e.stopPropagation()}>
        <Dropdown
          menu={{ items: menuItems }}
          trigger={['click', 'hover']}
        >
          <Button
            data-testid="actions-button"
            type="link"
          >
            <FontAwesomeIcon icon={faEllipsisH} />
          </Button>
        </Dropdown>
      </ButtonsColumn>
    </TableRow>
  );
}

PayrollTableRow.propTypes = {
  payroll: PropTypes.shape(),
  onEditClick: PropTypes.func.isRequired,
  onTransmitClick: PropTypes.func.isRequired,
  onEarlyPayment: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  onBaixarArquivoClick: PropTypes.func.isRequired,
  canShowApdataInfo: PropTypes.bool,
};

PayrollTableRow.defaultProps = {
  payroll: undefined,
  isAdmin: false,
  canShowApdataInfo: false,
};

export default PayrollTableRow;
